import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import GrowerDashboard from '@/pages/GrowerDashboard';
import CompostingSupervisorDashboard from '@/pages/CompostingSupervisorDashboard';
import GrowingSupervisorDashboard from '@/pages/GrowingSupervisorDashboard';
import OwnerDashboard from '@/pages/OwnerDashboard';
import TemperatureTechDashboard from '@/pages/TemperatureTechDashboard';
import WaterTechDashboard from '@/pages/WaterTechDashboard';
import '@/App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F9FAF9]">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  const DashboardRouter = () => {
    if (!user) return <Navigate to="/login" replace />;
    
    switch (user.role) {
      case 'grower':
        return <Navigate to="/grower" replace />;
      case 'composting_supervisor':
        return <Navigate to="/composting-supervisor" replace />;
      case 'growing_supervisor':
        return <Navigate to="/growing-supervisor" replace />;
      case 'owner':
        return <Navigate to="/owner" replace />;
      case 'temperature_tech':
      case 'temp_tech':
        return <Navigate to="/temperature" replace />;
      case 'water_tech':
        return <Navigate to="/watering" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={user ? <DashboardRouter /> : <Login onLogin={handleLogin} />}
          />
          <Route
            path="/grower"
            element={
              <ProtectedRoute allowedRoles={['grower']}>
                <GrowerDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/composting-supervisor"
            element={
              <ProtectedRoute allowedRoles={['composting_supervisor']}>
                <CompostingSupervisorDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/growing-supervisor"
            element={
              <ProtectedRoute allowedRoles={['growing_supervisor']}>
                <GrowingSupervisorDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/owner"
            element={
              <ProtectedRoute allowedRoles={['owner']}>
                <OwnerDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/temperature"
            element={
              <ProtectedRoute allowedRoles={['temperature_tech', 'temp_tech']}>
                <TemperatureTechDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/watering"
            element={
              <ProtectedRoute allowedRoles={['water_tech']}>
                <WaterTechDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<DashboardRouter />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;